import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { t } from 'i18next';

import {
  FORM_TABLE_COLUMN_NAME,
  FORM_TABLE_FIRST_COLUMN_NAME,
} from 'components/_basic/Form/FormTableComponent/constants';

import { getAdditionalColumnProperties } from '../../helpers';

export const FIRST_COLUMN_WIDTH = 342;
export const FIRST_COLUMN_MAXLENGTH = 150;
export const MAX_FIELD_LENGTH = 2;

export const COST_OF_SALES_FORM_NAMES = {
  CONST_OF_SALES_CURRENCY: 'costOfSalesCurrency',
  COST_OF_SALES_TABLE: 'costOfSalesTable',
  COST_OF_SALES_TABLE_TOTAL: 'totalCostOfSalesTable',
} as const;

export const useGetItemsColumnDefaultValues = () => {
  const { t, i18n } = useTranslation();

  return useMemo(
    () => [
      'Salaries for production staff',
      'Benefits and payroll taxes for production staff',
      'Depreciation of fixed assets',
      'Amortization of Intangible Assets',
      t('pdfPages.mappings.financialPlan.costOfSalesForm.trainingAndCertificationOfStaff'),
      t('pdfPages.mappings.financialPlan.costOfSalesForm.insuranceCosts'),
      t('pdfPages.mappings.financialPlan.costOfSalesForm.utilitiesForProductionFacilities'),
      t('pdfPages.mappings.financialPlan.costOfSalesForm.manufacturingOverhead'),
      t('pdfPages.mappings.financialPlan.costOfSalesForm.transportationAndLogistics'),
      t('pdfPages.mappings.financialPlan.costOfSalesForm.subcontractorServices'),
      t('pdfPages.mappings.financialPlan.costOfSalesForm.rentForProductionFacilities'),
      t('pdfPages.mappings.financialPlan.costOfSalesForm.softwareForProduction'),
      t('pdfPages.mappings.financialPlan.costOfSalesForm.maintenanceOfProductionEquipment'),
      t('pdfPages.mappings.financialPlan.costOfSalesForm.researchAndDevelopmentCostsRelatedToProduction'),
      t('pdfPages.mappings.financialPlan.costOfSalesForm.licensingFeesAndRoyalties'),
      t('pdfPages.mappings.financialPlan.costOfSalesForm.technicalSupportAndMaintenance'),
      t('pdfPages.mappings.financialPlan.costOfSalesForm.other'),
    ],
    [t, i18n],
  );
};

export const itemsColumnDefaultValues = [
  'Salaries for production staff',
  'Benefits and payroll taxes for production staff',
  'Depreciation of fixed assets',
  'Amortization of Intangible Assets',
  t('pdfPages.mappings.financialPlan.costOfSalesForm.trainingAndCertificationOfStaff'),
  t('pdfPages.mappings.financialPlan.costOfSalesForm.insuranceCosts'),
  t('pdfPages.mappings.financialPlan.costOfSalesForm.utilitiesForProductionFacilities'),
  t('pdfPages.mappings.financialPlan.costOfSalesForm.manufacturingOverhead'),
  t('pdfPages.mappings.financialPlan.costOfSalesForm.transportationAndLogistics'),
  t('pdfPages.mappings.financialPlan.costOfSalesForm.subcontractorServices'),
  t('pdfPages.mappings.financialPlan.costOfSalesForm.rentForProductionFacilities'),
  t('pdfPages.mappings.financialPlan.costOfSalesForm.softwareForProduction'),
  t('pdfPages.mappings.financialPlan.costOfSalesForm.maintenanceOfProductionEquipment'),
  t('pdfPages.mappings.financialPlan.costOfSalesForm.researchAndDevelopmentCostsRelatedToProduction'),
  t('pdfPages.mappings.financialPlan.costOfSalesForm.licensingFeesAndRoyalties'),
  t('pdfPages.mappings.financialPlan.costOfSalesForm.technicalSupportAndMaintenance'),
  t('pdfPages.mappings.financialPlan.costOfSalesForm.other'),
];

export const costOfSalesColumns = [
  {
    title: 'financialPlan.plannedExpenses.costOfSales.columnTitles.plannedMonthlyExpenses',
    id: 'financialPlan.plannedExpenses.costOfSales.columnTitles.plannedMonthlyExpenses',
  },
  {
    title: 'financialPlan.plannedExpenses.costOfSales.columnTitles.annualExpenseGrowthRate',
    id: 'financialPlan.plannedExpenses.costOfSales.columnTitles.annualExpenseGrowthRate',
  },
];

const additionalProperties: Record<string, string> = getAdditionalColumnProperties(
  FORM_TABLE_COLUMN_NAME,
  MAX_FIELD_LENGTH,
);

export const defaultCostOfSalesItem = { [FORM_TABLE_FIRST_COLUMN_NAME]: '', ...additionalProperties };

export const getDefaultCostOfSalesRows = (defaultValues: string[]) =>
  defaultValues.map((item) => {
    return { [FORM_TABLE_FIRST_COLUMN_NAME]: item, ...additionalProperties };
  });

export type IRow = Record<string, string | number | undefined>;
export interface ICostOfSalesValuesType {
  [COST_OF_SALES_FORM_NAMES.CONST_OF_SALES_CURRENCY]: string;
  [COST_OF_SALES_FORM_NAMES.COST_OF_SALES_TABLE]: IRow[];
  [COST_OF_SALES_FORM_NAMES.COST_OF_SALES_TABLE_TOTAL]: IRow;
}

export const useGetDefaultCostOfSalesRows = () => {
  const defaultValues = useGetItemsColumnDefaultValues();

  return useMemo(() => getDefaultCostOfSalesRows(defaultValues), [defaultValues]);
};

export const useGetCostOfSalesInitialValues = (): ICostOfSalesValuesType => {
  const defaultRows = useGetDefaultCostOfSalesRows();

  return useMemo(
    () => ({
      [COST_OF_SALES_FORM_NAMES.CONST_OF_SALES_CURRENCY]: DIMENSIONALITY_VALUES.THOUSAND,
      [COST_OF_SALES_FORM_NAMES.COST_OF_SALES_TABLE]: defaultRows,
      [COST_OF_SALES_FORM_NAMES.COST_OF_SALES_TABLE_TOTAL]: defaultCostOfSalesItem,
    }),
    [defaultRows],
  );
};

export const costOfSalesInitialValues = {
  [COST_OF_SALES_FORM_NAMES.CONST_OF_SALES_CURRENCY]: DIMENSIONALITY_VALUES.THOUSAND,
  [COST_OF_SALES_FORM_NAMES.COST_OF_SALES_TABLE]: getDefaultCostOfSalesRows(itemsColumnDefaultValues),
  [COST_OF_SALES_FORM_NAMES.COST_OF_SALES_TABLE_TOTAL]: defaultCostOfSalesItem,
};
